import { format, getYear, parseISO } from 'date-fns';
import { get as _get } from 'lodash';
import { PostMessageCodes } from '../Constants/PostMessageCodes';
import { ShopsInfo } from '../model/shops.response';
import {
  AutoEligibilityServicesInfo,
  Features,
  GenericProductType,
  OpCo,
  ServiceType,
  SourceSystem,
  StageNames,
  StageState,
} from '../types/type';

// a helper function for date parsing & formatting
export function parseAndFormatDate(dateStr: string): { date: Date; formatted: string } {
  let date = new Date();
  if (dateStr.length > 16 && dateStr[dateStr.length - 9] === ':') {
    date = parseISO(dateStr.slice(0, dateStr.length - 9));
  } else if (dateStr) {
    date = new Date(dateStr);
  }
  return { date, formatted: format(date, 'MM/dd/yyyy') };
}

export const splitTextByWord = (text: string, word: string | string[]) => {
  const words = Array.isArray(word) ? word : [word];
  const boldSet = new Set(words.map((w) => w.toLowerCase()));
  const parts: string[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    let earliestIndex = -1;
    let matchedWord = '';
    for (const w of words) {
      const idx = remaining.toLowerCase().indexOf(w.toLowerCase());
      if (idx !== -1 && (earliestIndex === -1 || idx < earliestIndex)) {
        earliestIndex = idx;
        matchedWord = w;
      }
    }
    if (earliestIndex === -1) {
      parts.push(remaining);
      break;
    }
    if (earliestIndex > 0) {
      parts.push(remaining.substring(0, earliestIndex));
    }
    parts.push(remaining.substring(earliestIndex, earliestIndex + matchedWord.length));
    remaining = remaining.substring(earliestIndex + matchedWord.length);
  }

  return { parts, boldSet };
};

export const truncateFileName = (fileName?: string, maxLength = 50) => {
  if (!fileName) return '';
  if (fileName.length <= maxLength) return fileName;

  const charsToShow = maxLength - 3; // 3 chars for "..."
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  const start = fileName.slice(0, frontChars);
  const end = fileName.slice(-backChars);

  return `${start}...${end}`;
};

export function isWebViewOrSafari() {
  const userAgent = navigator.userAgent;
  const isSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|Android/i.test(userAgent);
  const isIOSWKWebView =
    /AppleWebKit/i.test(userAgent) && /Mobile/i.test(userAgent) && /iP(hone|ad|od)/i.test(userAgent);
  /* Just commenting out Android browser check for future reference*/
  // const isAndroidWebView =
  //   /wv/i.test(userAgent) || /Android.*Chrome\/[\d.]+ Mobile/i.test(userAgent);
  return isSafari || isIOSWKWebView;
}

export function determineConversePartner(opco: string, partnerId: string, userRole: string, policyOpCo: string) {
  if (userRole === 'agent') {
    return opco + 'agency';
  } else if (userRole === 'adjuster') {
    return opco + 'adjuster';
  } else if (userRole === 'thirdParty') {
    return opco + 'ThirdParty';
  } else {
    return opco;
  }
}

/*
Brand opco for an AmFam-channel partner (customer group id). Only the theme uses it (app.tsx); everything else
uses the servicing opco (mapPartnerCodeToServicingOpcoId / getServicingOpCo).
*/
export function mapPartnerCodeToBrandOpcoId(opCo: string, partnerCode: string) {
  console.log(`mapPartnerCodeToBrandOpcoId [opCo: ${opCo}] [partnerCode: ${partnerCode}]`);
  if (opCo === OpCo.AMFAM) {
    switch (partnerCode) {
      case '660':
        return OpCo.LEMONADE;
      case '172': // Homesite
      case '16': // Ivantage
      case '658': // Amica
      case '603': // Kraft Lake
        return OpCo.HOMESITE;
      case '529': // MSA
      case '633': // RPS
        return OpCo.MSA;
      case '14':
        return OpCo.GEICO;
      case '4':
        return OpCo.PROGRESSIVE;
      default:
        return opCo;
    }
  } else {
    return opCo;
  }
}

/*
Servicing opco: what the app uses for copy, phone numbers, question sets, eligible services and navigation.
Lemonade shares the Homesite question set; every other AmFam-channel partner stays on amfam.
*/
export function mapPartnerCodeToServicingOpcoId(opCo: string, partnerCode: string) {
  if (opCo === OpCo.AMFAM && partnerCode === '660') {
    return OpCo.HOMESITE;
  }
  return opCo;
}

export interface ErrorInfo {
  error: string;
  statusCode: number;
  message: string;
}
export function postMessageToParentApp(
  messageType: string,
  claimNumber?: string,
  response?: ErrorInfo,
  policyNumber?: string
) {
  const finalMessageTypeName =
    response?.statusCode === 404 ? PostMessageCodes.DIGITAL_CLAIM_V2_SESSION_TIMEOUT_ERROR : messageType;
  const finalErrorInfo = {
    error: _get(response, 'error', ''),
    statusCode: _get(response, 'statusCode', ''),
    message: _get(response, 'message', ''),
  };
  window.parent.postMessage(
    {
      messageName: finalMessageTypeName,
      claimNumber: claimNumber ? claimNumber : '',
      errorInfo: finalErrorInfo,
      policyNumber: policyNumber ? policyNumber : '',
    },
    '*'
  );
}

export function getShortName(name: string) {
  let shortName = '';
  const names = name.split(' ');
  const firstName = names[0];
  const lastName = names[names.length - 1];
  shortName = firstName.slice(0, 1) + lastName.slice(0, 1);
  return shortName;
}

export function formatDate(date: string) {
  if (date) {
    const newDate = new Date(date);
    const dateWithOutTimeZone = new Date(
      newDate.valueOf() + newDate.getTimezoneOffset() * 60 * 1000
      // Reference: https://stackoverflow.com/questions/48172772/time-zone-issue-involving-date-fns-format
    );
    return format(dateWithOutTimeZone, 'MM/dd/yyyy');
  }
  return '';
}

export function formatTime(date: string) {
  if (date) {
    const formattedDate = format(new Date(date), 'h:mm a');
    return formattedDate;
  }
  return '';
}

export const formatDateAndTime = (createdDt: string): string => {
  return format(new Date(createdDt), "MM/dd/yyyy 'at' hh:mm a");
};

export function getMonthShortName(dateString: string) {
  let monthShortName = '';
  if (dateString) {
    const monthNumber = dateString.split('-')[1];
    monthShortName = months.filter((month) => month.value === monthNumber)[0].abbr.toUpperCase();
  }
  return monthShortName;
}

export function getDate(dateString: string) {
  let date = '';
  if (dateString) {
    date = dateString.split('-')[2];
  }
  return date;
}

export function getPhotoDate(dateString: string) {
  return format(new Date(dateString), 'P'); // format will convert the date displayed to the local timezone of the browser
}

export function getPrettyServiceName(serviceTypeName: string) {
  let serviceName = '';
  switch (serviceTypeName) {
    case ServiceType.EMERGENCY_SERVICE_TYPE:
      serviceName = StageNames.EMERGENCY_SERVICES;
      break;
    case ServiceType.WATER_MITIGATION_TYPE:
      serviceName = StageNames.WATER_REMOVAL;
      break;
    default:
      break;
  }
  return serviceName;
}

export function getPrettyPhoneNumber(unformattedPhoneNumber: string | null, nobrackets?: boolean): string {
  let formattedPhoneNumber = '';
  if (!unformattedPhoneNumber) {
    return formattedPhoneNumber;
  }
  if (unformattedPhoneNumber) {
    let numbersOnly = unformattedPhoneNumber.replace(/[^0-9]/g, '');
    // Check for international code in the number. If present, remove it.
    if (numbersOnly && numbersOnly.length === 11) {
      numbersOnly = numbersOnly.substring(1, 11);
    }
    if (numbersOnly && numbersOnly.length === 10) {
      const areaCode = numbersOnly.substring(0, 3);
      const prefix = numbersOnly.substring(3, 6);
      const lineNumber = numbersOnly.substring(6, 10);
      if (nobrackets) {
        formattedPhoneNumber = areaCode + '-' + prefix + '-' + lineNumber;
        return formattedPhoneNumber;
      }
      formattedPhoneNumber = '(' + areaCode + ') ' + prefix + '-' + lineNumber;
      return formattedPhoneNumber;
    }
  }
  return formattedPhoneNumber;
}

export function getClaimStageText(statusCode: string) {
  let statusText = '';
  const normalizedStatusCode = statusCode.toUpperCase();
  switch (normalizedStatusCode) {
    case 'ARCHIVED':
    case 'CLOSED':
    case 'VOID':
    case 'CLOSED_AUTO_PAY':
    case 'CLOSED_ERROR':
    case 'REOPEN':
      statusText = 'Closed';
      break;
    case 'DRAFT':
    case 'OPEN':
    case 'IN_PROGRESS':
    case 'OPEN_REPORTED':
    case 'AUTOPAY_PENDING':
    case 'AUTOPAY_REVIEW':
    case 'AWAITING_SETUP':
      statusText = 'Submit';
      break;
  }
  return statusText;
}

export function getPrettyClaimNumber(unformatttedNumber: string | null): string {
  if (!unformatttedNumber) {
    return '';
  }
  const cleanedNumber = unformatttedNumber.replace(/-/g, '');
  return cleanedNumber.substring(0, 2) + '-' + cleanedNumber.substring(2, 5) + '-' + cleanedNumber.substring(5);
}

export function getUnformattedClaimNumber(formatttedNumber: string | null): string {
  if (!formatttedNumber) {
    return '';
  }
  const unformattedNumber = formatttedNumber.replace(/-/g, '');
  return unformattedNumber;
}

// Draft (not yet submitted) claims carry a temporary ClaimCenter number prefixed with 'TC', e.g. TC-021-234025.
export function isDraftClaimNumber(claimNumber: string | null | undefined): boolean {
  return getUnformattedClaimNumber(claimNumber ?? null)
    .toUpperCase()
    .startsWith('TC');
}

export function getNextStageInfo(currentStageId: string, availableProgressStages: StageState[]) {
  return getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== currentStageId && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];
}

export function getStagesInOrder(stageStates: StageState[]) {
  return stageStates.sort((a, b) =>
    (a.completed ? a.orderOfDisplay - 3 : a.orderOfDisplay) > (b.completed ? b.orderOfDisplay - 3 : b.orderOfDisplay)
      ? 1
      : -1
  );
}

export function getNextView(viewTypeName: string): string {
  let destinationPath = '';
  switch (viewTypeName) {
    case Features.EMERGENCY_SERVICES:
      destinationPath = '/emergency-services-home';
      break;
    case Features.WATER_REMOVAL:
      destinationPath = '/emergency-water-removal-home';
      break;
    case Features.DOCUMENT_DAMAGE:
      destinationPath = '/document-damage-services-home';
      break;
    case Features.COMPLETE_STAGE:
      destinationPath = '/final-services-confirmation';
      break;
    case Features.REPAIR:
      destinationPath = '/repair-home';
      break;
    case Features.PHOTOS:
      destinationPath = '/photos-home';
      break;
    case Features.RENTAL:
      destinationPath = '/rental-home';
      break;
    default:
      break;
  }
  return destinationPath;
}

export function getWebUrl(providerId: string): string {
  let url = '';
  switch (providerId) {
    case 'alacrity':
      url = 'https://www.alacritysolutions.com/';
      break;
    case 'altimeter':
      url = 'https://altimetersolutionsgroup.com/';
      break;
    case 'servpro':
      url = 'http://www.servpro.com/';
      break;
    case 'servicemaster':
      url = 'https://www.servicemaster.com/';
      break;
    case 'brightserv':
      url = 'https://www.genpact.com/solutions/managed-repairs-service';
      break;
    default:
      break;
  }
  return url;
}

export function getSourceSystem(opco: string): string {
  switch (opco) {
    case OpCo.MSA:
      return SourceSystem.MSA;
    case OpCo.AMFAM:
      return SourceSystem.AMFAM;
    default:
      return SourceSystem.AMFAM;
  }
}

export function getProviderIDfromDisplayName(displayName: string): string {
  let providerId = '';
  const providers = ['alacrity', 'altimeter', 'servpro', 'servicemaster', 'brightserv'];
  providers.forEach((provider) => {
    const matchedCollection = displayName.toLowerCase().match(provider.toLowerCase());
    if (matchedCollection && matchedCollection.length > 0) {
      providerId = provider;
      return;
    }
  });
  return providerId;
}

const months = [
  {
    name: 'January',
    abbr: 'Jan',
    value: '01',
  },
  {
    name: 'February',
    abbr: 'Feb',
    value: '02',
  },
  {
    name: 'March',
    abbr: 'Mar',
    value: '03',
  },
  {
    name: 'April',
    abbr: 'Apr',
    value: '04',
  },
  {
    name: 'May',
    abbr: 'May',
    value: '05',
  },
  {
    name: 'June',
    abbr: 'Jun',
    value: '06',
  },
  {
    name: 'July',
    abbr: 'Jul',
    value: '07',
  },
  {
    name: 'August',
    abbr: 'Aug',
    value: '08',
  },
  {
    name: 'September',
    abbr: 'Sep',
    value: '09',
  },
  {
    name: 'October',
    abbr: 'Oct',
    value: '10',
  },
  {
    name: 'November',
    abbr: 'Nov',
    value: '11',
  },
  {
    name: 'December',
    abbr: 'Dec',
    value: '12',
  },
];

export function getValidVehicleYears(): number[] {
  const start = 1982;
  const end = getYear(new Date()) + 1;
  const validYears = [];
  for (let i = start; i <= end; i++) {
    validYears.push(i);
  }
  return validYears.reverse();
}

export function getStaticProgressStages() {
  const stageStates: StageState[] = [];
  const reportStage: StageState = {
    name: StageNames.SUBMIT, // Set by default
    completed: true, // Set by default
    viewActive: false, // Set by default
    skipped: false, // Set by default
    showProgress: true, // Set by UI action
    orderOfDisplay: 0, // Set by default
    id: StageNames.SUBMIT, // Set by default
  };
  stageStates.push(reportStage);
  const evaluateStage: StageState = {
    name: StageNames.EVALUATE, // Set by default
    completed: false, // Set by default
    viewActive: true, // Set by default
    skipped: false, // Set by default
    showProgress: false, // Set by UI action
    orderOfDisplay: 1, // Set by default
    id: StageNames.EVALUATE, // Set by default
  };
  stageStates.push(evaluateStage);
  const resolveStage: StageState = {
    name: StageNames.RESOLVE, // Set by default
    completed: false, // Set by default
    viewActive: false, // Set by default
    skipped: false, // Set by default
    showProgress: false, // Set by UI action
    orderOfDisplay: 2, // Set by default
    id: StageNames.RESOLVE, // Set by default
  };
  stageStates.push(resolveStage);
  const closeStage: StageState = {
    name: StageNames.CLOSE, // Set by default
    completed: false, // Set by default
    viewActive: false, // Set by default
    skipped: false, // Set by default
    showProgress: false, // Set by UI action
    orderOfDisplay: 3, // Set by default
    id: StageNames.CLOSE, // Set by default
  };
  stageStates.push(closeStage);
  const orderedStages = getStagesInOrder(stageStates);
  return orderedStages;
}

export const getAddressLine = (shop: ShopsInfo) => {
  let address = '';
  address = `${shop.address.addressLine1}${shop.address.addressLine2}, ${shop.address.city}, ${shop.address.state} ${shop.address.zipCode}`;
  return address;
};

export const getShortHoursOfOperation = (hours: string) => {
  const shortString = hours
    .replace('Sunday', 'Sun')
    .replace('Monday', 'Mon')
    .replace('Tuesday', 'Tue')
    .replace('Wednesday', 'Wed')
    .replace('Thursday', 'Thu')
    .replace('Friday', 'Fri')
    .replace('Saturday', 'Sat')
    .replace(/ AM/g, ' am')
    .replace(/ PM/g, ' pm');
  return shortString;
};

export const getClaimType = (policyType: string) => {
  if (!policyType) {
    return GenericProductType.Other;
  }
  switch (policyType.toLowerCase()) {
    case 'personalauto':
    case 'boat_afi':
    case 'cycle_afi':
    case 'nonowners_afi':
    case 'misc_afi':
    case 'maacbasgnrisk_afi':
    case 'assignedrisk_afi':
    case 'motorhome':
    case 'nonownerveh':
    case 'miscvehpolicy':
    case 'miscveh':
    case 'famcar':
    case 'boat':
    case 'motorcyclepolicy':
      return GenericProductType.Auto;
    case 'businessauto':
    case 'commercialauto':
      return GenericProductType.CommercialAuto;
    case 'homeowners_afi':
    case 'manufactured_homeowners':
    case 'personalproperty':
    case 'homeowners':
    case 'homeownersline_hoe':
    case 'homeownersform1_afi':
    case 'homeownersform2_afi':
    case 'homeownersform3_afi':
    case 'homeownersform3_ext':
    case 'homeownersform4_afi':
    case 'homeownersform6_afi':
    case 'homeownersform6_ext':
    case 'homeownersformgs_afi':
    case 'homeownersformgs_ext':
    case 'homeownersgs_ext':
    case 'homeownersgs_afi':
    case 'renters_afi':
    case 'renters':
    case 'customvalueform1_afi':
    case 'customvalueform3_afi':
    case 'dwellingbasic_afi':
    case 'dwellingbroad_afi':
    case 'dwellingspecial_ext':
    case 'mobhomeseasonal_afi':
    case 'mobilehomeform1_afi':
    case 'mobilehomeform3_afi':
    case 'mobilehomegs_afi':
    case 'condo_afi':
    case 'condominium_unit-owners':
    case 'manufacturedhome_afi':
    case 'secondhome_afi':
    case 'tenantliability_ext':
    case 'dp1':
    case 'dp2':
    case 'dpm':
    case 'mh3':
    case 'ho4':
    case 'ho3':
    case 'ho5':
    case 'ho6':
    case 'formho1':
    case 'formho2':
    case 'formho3':
    case 'formho6':
    case 'formho4':
    case 'formhf9':
      return GenericProductType.Home;
    case 'bophab_afi':
    case 'bkp_afi':
    case 'businessowners':
    case 'businessownerspolicy':
    case 'businesskeypolicy':
    case 'cpp_afi':
    case 'contractorspackageprogram':
    case 'commercialpackage':
    case 'vltbopp_afi':
    case 'commercialproperty':
    case 'commercialpackagepolicy':
      return GenericProductType.CommercialProperty;
    case 'farmranch_afi':
    case 'farmranch':
    case 'farmranchumb_afi':
      return GenericProductType.FarmRanch;
    case 'generalliability':
    case 'frumbrella':
    case 'fidelity_ext':
    case 'commercialumbrella':
    case 'eco_ext':
    case 'suretycontract_ext':
    case 'suretyother_ext':
    case 'nonprofitdirectionsandofficers':
    case 'd_and_o':
      return GenericProductType.GeneralLiability;
    case 'workerscomp':
    case 'workerscompensation':
    case 'workers_comp':
    case 'workers_compensation':
      return GenericProductType.WorkersComp;
    default:
      return GenericProductType.Other;
  }
};

/**
 * Determines if a claim should use 'auto' LOB for converse API calls.
 * Returns true for both personal auto and commercial auto policies.
 *
 * @param claim - The claim object containing policyType
 * @returns true if policy should use 'auto' LOB, false otherwise (property LOB)
 */
export const isPolicyTypeAuto = (claim: any): boolean => {
  const policyType = _get(claim, 'policyType');
  if (!policyType) {
    return false;
  }
  const claimType = getClaimType(policyType);
  return claimType === GenericProductType.Auto || claimType === GenericProductType.CommercialAuto;
};

export const mapAutoClaimDetail = (incidentInfo: any) => {
  const firstPartyIncidents = incidentInfo.filter(
    (incident: any) => incident.vehicleSum.filter((vehicle: any) => vehicle.insuredLoss === 'insured').length > 0
  );

  if (firstPartyIncidents.length > 0) {
    return [
      {
        make: _get(firstPartyIncidents[0], 'vehicleSum[0].basicVehicleData.Make', ''),
        model: _get(firstPartyIncidents[0], 'vehicleSum[0].basicVehicleData.Model', ''),
        year: _get(firstPartyIncidents[0], 'vehicleSum[0].basicVehicleData.Year', ''),
        vin: _get(firstPartyIncidents[0], 'vehicleSum[0].basicVehicleData.VIN', ''),
      },
    ];
  } else {
    return [];
  }
};

export const isAutoEligibleService = (object: any): object is AutoEligibilityServicesInfo => {
  return 'vin' in object;
};

export const getAutoShopAddress = (specialist: any) => {
  const address = _get(specialist, 'address.claimContact.addresses[0]');

  if (address) {
    return (
      _get(address, 'addressLine1', '') +
      ' ' +
      _get(address, 'city', '') +
      ', ' +
      _get(address, 'state', '') +
      ' ' +
      _get(address, 'postalCode', '')
    );
  } else {
    return '';
  }
};

export const usStates: { name: string; value: string }[] = [
  {
    name: 'Alabama',
    value: 'AL',
  },
  {
    name: 'Alaska',
    value: 'AK',
  },
  {
    name: 'Arizona',
    value: 'AZ',
  },
  {
    name: 'Arkansas',
    value: 'AR',
  },
  {
    name: 'California',
    value: 'CA',
  },
  {
    name: 'Colorado',
    value: 'CO',
  },
  {
    name: 'Connecticut',
    value: 'CT',
  },
  {
    name: 'Delaware',
    value: 'DE',
  },
  {
    name: 'District Of Columbia',
    value: 'DC',
  },
  {
    name: 'Florida',
    value: 'FL',
  },
  {
    name: 'Georgia',
    value: 'GA',
  },
  {
    name: 'Hawaii',
    value: 'HI',
  },
  {
    name: 'Idaho',
    value: 'ID',
  },
  {
    name: 'Illinois',
    value: 'IL',
  },
  {
    name: 'Indiana',
    value: 'IN',
  },
  {
    name: 'Iowa',
    value: 'IA',
  },
  {
    name: 'Kansas',
    value: 'KS',
  },
  {
    name: 'Kentucky',
    value: 'KY',
  },
  {
    name: 'Louisiana',
    value: 'LA',
  },
  {
    name: 'Maine',
    value: 'ME',
  },
  {
    name: 'Maryland',
    value: 'MD',
  },
  {
    name: 'Massachusetts',
    value: 'MA',
  },
  {
    name: 'Michigan',
    value: 'MI',
  },
  {
    name: 'Minnesota',
    value: 'MN',
  },
  {
    name: 'Mississippi',
    value: 'MS',
  },
  {
    name: 'Missouri',
    value: 'MO',
  },
  {
    name: 'Montana',
    value: 'MT',
  },
  {
    name: 'Nebraska',
    value: 'NE',
  },
  {
    name: 'Nevada',
    value: 'NV',
  },
  {
    name: 'New Hampshire',
    value: 'NH',
  },
  {
    name: 'New Jersey',
    value: 'NJ',
  },
  {
    name: 'New Mexico',
    value: 'NM',
  },
  {
    name: 'New York',
    value: 'NY',
  },
  {
    name: 'North Carolina',
    value: 'NC',
  },
  {
    name: 'North Dakota',
    value: 'ND',
  },
  {
    name: 'Ohio',
    value: 'OH',
  },
  {
    name: 'Oklahoma',
    value: 'OK',
  },
  {
    name: 'Oregon',
    value: 'OR',
  },
  {
    name: 'Pennsylvania',
    value: 'PA',
  },
  {
    name: 'Rhode Island',
    value: 'RI',
  },
  {
    name: 'South Carolina',
    value: 'SC',
  },
  {
    name: 'South Dakota',
    value: 'SD',
  },
  {
    name: 'Tennessee',
    value: 'TN',
  },
  {
    name: 'Texas',
    value: 'TX',
  },
  {
    name: 'Utah',
    value: 'UT',
  },
  {
    name: 'Vermont',
    value: 'VT',
  },
  {
    name: 'Virginia',
    value: 'VA',
  },
  {
    name: 'Washington',
    value: 'WA',
  },
  {
    name: 'West Virginia',
    value: 'WV',
  },
  {
    name: 'Wisconsin',
    value: 'WI',
  },
  {
    name: 'Wyoming',
    value: 'WY',
  },
];

export const trustedOrigins = [
  'https://cccphotoupload-dev.amfam.com',
  'https://cccphotoupload-int.amfam.com',
  'https://cccphotoupload.amfam.com',
];

/**
 *
 * @param phoneNumber
 * @returns empty string if phone number is all 9's
 */
export const checkAndReplaceDummyPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber) {
    return phoneNumber.replace(/[^\d]/g, '') === '9999999999' ? '' : phoneNumber;
  }
  return '';
};
